import React from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Avatar,
  Button,
} from '@mui/material';
import {
  Star,
  LocalDining,
  AccessTime,
} from '@mui/icons-material';
import { foodCategories, featuredItems } from '../data';
import { getIconComponent } from '../App';
import { FeaturedItem } from '../types';

// Custom styles for slider
const sliderStyles = `
  .slick-slide {
    height: 500px;
  }
  .slick-slide img {
    width: 100%;
    height: auto;
  }
  .slick-dots {
    bottom: 30px;
    z-index: 15;
  }
  .slick-dots li button:before {
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px;
  }
  .slick-dots li.slick-active button:before {
    color: white;
    font-size: 12px;
  }
  .slick-prev,
  .slick-next {
    z-index: 10;
    width: 50px;
    height: 50px;
    top: 50%;
    transform: translateY(-50%);
  }
  .slick-prev:before,
  .slick-next:before {
    color: white;
    font-size: 28px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  }
  .slick-prev {
    left: 20px;
  }
  .slick-next {
    right: 20px;
  }
`;

const services = [
  {
    icon: <LocalDining />,
    title: 'Quality Food',
    description: 'Fresh ingredients and authentic recipes',
  },
  {
    icon: <AccessTime />,
    title: 'Fast Delivery',
    description: 'Quick delivery to your doorstep',
  },
  {
    icon: <Star />,
    title: 'Excellent Service',
    description: 'Professional and friendly staff',
  },
];

const HomePage: React.FC = () => {
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
  };

  const sliderContent = [
    {
      title: 'Delicious \nMasala Dosha',
      description: 'Experience the best food in town with our premium ingredients and authentic recipes. Order now and taste the difference!',
      image: '/assets/images/post-1.jpg',
     
    },
    {
      title: 'Fresh Ingredients\nAmazing Taste',
      description: 'We use only the freshest, locally sourced ingredients to create dishes that are bursting with flavor.',
      image: '/assets/images/post-2.jpg',
     
    },
    {
      title: 'Quick Delivery\nHot & Ready',
      description: 'Get your favorite meals delivered hot and fresh to your doorstep in 30 minutes or less.',
      image: '/assets/images/post-3.jpg',
     
    },
  ];

  return (
    <>
      <style>{sliderStyles}</style>
      {/* Hero Section with Slider */}
      <Box
        sx={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('/assets/images/hero-bg.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Slider {...sliderSettings}>
          {sliderContent.map((slide, index) => (
            <Box key={index}>
              <Container maxWidth="lg">
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h1" sx={{ mb: 3, fontWeight: 700 }}>
                      {slide.title}
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                      {slide.description}
                    </Typography>
                    
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      component="img"
                      src={slide.image}
                      alt="Hero Food"
                      sx={{
                        width: '100%',
                        height: 500,
                        objectFit: 'cover',
                        borderRadius: 4,
                        boxShadow: 4,
                      }}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Box>
          ))}
        </Slider>
      </Box>

      {/* Categories Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 600 }}>
          Food Categories
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Explore our wide variety of delicious food options
        </Typography>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {foodCategories.map((category, index) => (
            <Grid item xs={6} sm={4} md={2.4} key={category.id}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 160,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4,
                  },
                }}
                component={Link}
                to="/menu"
              >
                <Box
                  sx={{
                    bgcolor: category.color,
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'white',
                  }}
                >
                  {getIconComponent(category.icon)}
                </Box>
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                  {category.displayName}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Featured Items */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 600 }}>
            Featured Items
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Our most popular and delicious dishes
          </Typography>
          <Grid container spacing={4}>
            {featuredItems.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={`/assets/images/${item.image}`}
                    alt={item.name}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {item.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Star sx={{ color: '#ffc107', fontSize: 16 }} />
                      <Typography variant="body2">{item.rating}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        • {item.prepTime}
                      </Typography>
                    </Box>
                    
                  </CardContent>
                  
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 600 }}>
          Why Choose Gudiya Cooking Recipe
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          We provide the best food and service experience
        </Typography>
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    fontSize: 40,
                  }}
                >
                  {service.icon}
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {service.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {service.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default HomePage;
